import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaIdCard,
  FaUserShield,
  FaEdit,
  FaSave,
  FaTimes,
  FaSpinner,
  FaCalendar,
  FaTint,
  FaBuilding,
  FaGlobe,
  FaCamera,
  FaInstagram,
  FaYoutube,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaTiktok,
  FaExclamationTriangle,
  FaSync,
} from "react-icons/fa";

const InfluencerProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line
  }, []);

  const fetchProfileData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/influencer/get/icard`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setProfileData(response.data.data);
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditData(profileData);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      setProfileData(editData);
      setEditing(false);
    } catch (error) {
      console.error("Error saving profile:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({});
    setEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <FaSpinner className="animate-spin text-purple-600 text-3xl" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="bg-white p-6 rounded-2xl shadow-sm border text-center max-w-sm w-full">
          <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-2">
            Profile Not Available
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            Your profile will appear after verification.
          </p>
          <button
            onClick={fetchProfileData}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm"
          >
            <FaSync className="inline mr-2" />
            Refresh
          </button>
        </div>
      </div>
    );
  }

  const currentData = editing ? editData : profileData;

  return (
    <div className="min-h-screen bg-gray-50 px-2 sm:px-4 py-4 md:py-6">
      <div className="max-w-5xl mx-auto">

        {/* Compact Header */}
        <div className="bg-white rounded-3xl shadow-sm border mb-4 overflow-hidden">
          <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-indigo-700 px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                  <FaUser className="text-white text-lg" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl font-black text-white tracking-tight">
                    Influencer Hub
                  </h1>
                  <p className="text-purple-100 text-[10px] sm:text-xs font-bold uppercase tracking-wider opacity-80">
                    Verify & Manage Profile
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                {!editing ? (
                  <button
                    onClick={handleEdit}
                    className="px-4 py-1.5 text-[11px] font-black uppercase tracking-widest bg-white text-purple-700 rounded-xl transition-all hover:bg-purple-50 active:scale-95 shadow-sm"
                  >
                    <FaEdit className="inline mr-2 mb-0.5" />
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="px-3 py-1.5 text-[11px] font-black uppercase tracking-widest bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-1.5 text-[11px] font-black uppercase tracking-widest bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all active:scale-95 shadow-lg shadow-emerald-500/20"
                    >
                      {saving ? (
                        <FaSpinner className="animate-spin inline mr-2" />
                      ) : (
                        <FaSave className="inline mr-2 mb-0.5" />
                      )}
                      Save
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Compact Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

          {/* Profile Sidebar */}
          <div className="md:col-span-4 lg:col-span-3">
            <div className="bg-white rounded-3xl shadow-sm border p-6 text-center h-full">
              <div className="relative mx-auto w-24 h-24 mb-4">
                <div className="rounded-3xl overflow-hidden border-4 border-white shadow-xl w-full h-full rotate-3 group-hover:rotate-0 transition-transform duration-500 bg-slate-100">
                  {currentData?.image ? (
                    <img
                      src={currentData.image}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FaUser className="text-4xl text-slate-300" />
                    </div>
                  )}
                </div>

                {editing && (
                  <button className="absolute -bottom-1 -right-1 bg-purple-600 text-white p-2 rounded-2xl text-xs shadow-lg hover:scale-110 transition-transform">
                    <FaCamera />
                  </button>
                )}
              </div>

              <h2 className="text-lg font-black text-slate-900 tracking-tight leading-none mb-1">
                {currentData?.name}
              </h2>
              <p className="text-purple-600 text-[10px] font-black uppercase tracking-widest mb-3">
                {currentData?.designation}
              </p>

              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-black text-slate-400 uppercase tracking-tighter shadow-sm mb-6">
                <FaIdCard className="text-purple-500" />
                ID: {currentData?.iinsafId || "N/A"}
              </div>

              {/* Social Links */}
              {currentData?.channelLinks?.length > 0 && (
                <div className="pt-4 border-t border-slate-50">
                  <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest block mb-3">Connected Channels</span>
                  <div className="flex flex-wrap justify-center gap-2">
                    {currentData.channelLinks.map((link, index) => {
                      const platform = link.platform?.toLowerCase() || "";
                      const icon = platform.includes("instagram") ? <FaInstagram /> :
                        platform.includes("youtube") ? <FaYoutube /> :
                          platform.includes("facebook") ? <FaFacebook /> :
                            platform.includes("twitter") ? <FaTwitter /> :
                              platform.includes("linkedin") ? <FaLinkedin /> :
                                platform.includes("tiktok") ? <FaTiktok /> :
                                  <FaGlobe />;

                      return (
                        <a
                          key={index}
                          href={link.link || link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 rounded-xl bg-slate-50 hover:bg-purple-50 flex items-center justify-center text-slate-400 hover:text-purple-600 transition-all border border-slate-100 shadow-sm"
                        >
                          {icon}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Details Content Area */}
          <div className="md:col-span-8 lg:col-span-9">
            <div className="bg-white rounded-3xl shadow-sm border p-5 sm:p-6 h-full">

              <div className="flex items-center justify-between mb-6">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-6 bg-purple-600 rounded-full" />
                  Profile Intelligence
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

                {/* Email Card */}
                <div className="bg-slate-50/50 p-4 rounded-3xl border border-slate-100 transition-all hover:shadow-md hover:shadow-slate-100 group">
                  <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    <FaEnvelope className="text-purple-500 group-hover:scale-110 transition-transform" />
                    Authentication
                  </div>
                  {editing ? (
                    <input
                      type="email"
                      value={currentData?.email || ""}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="w-full bg-white px-3 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-purple-500/20 outline-none"
                    />
                  ) : (
                    <p className="text-sm font-bold text-slate-900 truncate">
                      {currentData?.email || "No Email Provided"}
                    </p>
                  )}
                </div>

                {/* Phone Card */}
                <div className="bg-slate-50/50 p-4 rounded-3xl border border-slate-100 transition-all hover:shadow-md hover:shadow-slate-100 group">
                  <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    <FaPhone className="text-emerald-500 group-hover:scale-110 transition-transform" />
                    Mobile Access
                  </div>
                  {editing ? (
                    <input
                      type="text"
                      value={currentData?.mobileNo || ""}
                      onChange={(e) => handleInputChange("mobileNo", e.target.value)}
                      className="w-full bg-white px-3 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-purple-500/20 outline-none"
                    />
                  ) : (
                    <p className="text-sm font-bold text-slate-900">
                      {currentData?.mobileNo || "N/A"}
                    </p>
                  )}
                </div>

                {/* Location Grid (City & State combined for compactness) */}
                <div className="bg-slate-50/50 p-4 rounded-3xl border border-slate-100 transition-all hover:shadow-md hover:shadow-slate-100 group">
                  <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    <FaMapMarkerAlt className="text-rose-500 group-hover:scale-110 transition-transform" />
                    Regional Node
                  </div>
                  <p className="text-sm font-bold text-slate-900">
                    {currentData?.city && currentData?.state ? `${currentData.city}, ${currentData.state}` : currentData?.city || currentData?.state || "N/A"}
                  </p>
                </div>

                {/* Blood Group */}
                <div className="bg-slate-50/50 p-4 rounded-3xl border border-slate-100 transition-all hover:shadow-md hover:shadow-slate-100 group">
                  <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    <FaTint className="text-red-600 group-hover:scale-110 transition-transform" />
                    Vital Type
                  </div>
                  <p className="text-sm font-bold text-slate-900">
                    {currentData?.bloodGroup || "O+ (Default)"}
                  </p>
                </div>

                {/* Join Date */}
                <div className="bg-slate-50/50 p-4 rounded-3xl border border-slate-100 transition-all hover:shadow-md hover:shadow-slate-100 group">
                  <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    <FaCalendar className="text-sky-500 group-hover:scale-110 transition-transform" />
                    Onboarding
                  </div>
                  <p className="text-sm font-bold text-slate-900">
                    {currentData?.createdAt ? new Date(currentData.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : "Recently"}
                  </p>
                </div>

                {/* Status Card (Always visible but static) */}
                <div className="bg-emerald-50/30 p-4 rounded-3xl border border-emerald-100/50 group">
                  <div className="flex items-center gap-2 text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1.5">
                    <FaUserShield className="group-hover:scale-110 transition-transform" />
                    Credentials
                  </div>
                  <p className="text-[10px] font-black text-emerald-600 uppercase tracking-tighter flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                    Identity Verified
                  </p>
                </div>

              </div>

              {/* Address Section - Full Width if available */}
              {currentData?.ResidentialAddress && (
                <div className="mt-4 bg-slate-50/50 p-4 rounded-3xl border border-slate-100">
                  <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    <FaMapMarkerAlt className="text-slate-400" />
                    Primary Residence
                  </div>
                  <p className="text-xs font-bold text-slate-700 leading-relaxed italic">
                    {currentData.ResidentialAddress}
                  </p>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default InfluencerProfile;